import { BrowserAuthError, EventType, InteractionStatus, type AuthenticationResult } from "@azure/msal-browser";
import { Browser } from "@capacitor/browser";
import { useMsal } from "@azure/msal-react";
import { ReactNode, useEffect, useState } from "react";
import { startLogin, clearMsalInteractionLock } from "./msal";
import { ANDROID_BUILD_LABEL, showAndroidBuildStamp } from "./buildStamp";
import { NATIVE_LOGIN_ERROR_KEY } from "./native";
import { isNativeShell } from "./platform";
import { grantNativeSession, hasNativeSession, subscribeNativeSession } from "./session";
import { acquireIdToken } from "./token";
import { AppFooter } from "../components/AppFooter";
import logoUrl from "../../imgs/logo_charm.png";

function loginErrorMessage(cause: unknown): string {
  if (cause instanceof Error && cause.message.trim()) {
    return cause.message;
  }
  return "Microsoft sign-in failed to open. Install Chrome (or a browser that supports Custom Tabs) and try again.";
}

function readNativeLoginError(): string | undefined {
  if (typeof sessionStorage === "undefined") {
    return undefined;
  }
  return sessionStorage.getItem(NATIVE_LOGIN_ERROR_KEY) ?? undefined;
}

export function LoginScreen({ error }: { error?: string }) {
  const { instance } = useMsal();
  const [clicked, setClicked] = useState(false);
  const [localError, setLocalError] = useState<string | undefined>(error ?? readNativeLoginError());
  const busy = clicked;
  const shownError = localError || error;

  useEffect(() => {
    const unlock = () => setClicked(false);
    window.addEventListener("ironfleet-auth-settled", unlock);
    return () => window.removeEventListener("ironfleet-auth-settled", unlock);
  }, []);

  useEffect(() => {
    if (!isNativeShell()) {
      return;
    }
    let handle: { remove: () => Promise<void> } | undefined;
    void Browser.addListener("browserFinished", () => {
      setClicked(false);
    }).then((listener) => {
      handle = listener;
    });
    return () => {
      void handle?.remove();
    };
  }, []);

  const onSignIn = async () => {
    if (busy) {
      return;
    }
    setClicked(true);
    setLocalError(undefined);
    sessionStorage.removeItem(NATIVE_LOGIN_ERROR_KEY);
    try {
      await startLogin(instance);
    } catch (cause) {
      if (cause instanceof BrowserAuthError && cause.errorCode === "interaction_in_progress") {
        clearMsalInteractionLock();
        setClicked(false);
        setLocalError("Sign-in was still running. Tap Sign in with Microsoft again.");
        return;
      }
      console.error(cause);
      setLocalError(loginErrorMessage(cause));
      setClicked(false);
    }
  };

  return (
    <div className="app-shell login-shell">
      <section className="card login-card">
        <img className="login-logo" src={logoUrl} alt="Charm" />
        <p className="brand-kicker">Show workspace</p>
        <h1 className="brand-title">Sign in</h1>
        <p className="brand-sub">
          Microsoft work or school account required (Ironfleet or invited guest). Passkeys appear on
          the Microsoft page when your account has one registered.
        </p>
        {shownError ? <p className="login-error">{shownError}</p> : null}
        <button type="button" onClick={() => void onSignIn()} disabled={busy}>
          {busy ? "Opening Microsoft…" : "Sign in with Microsoft"}
        </button>
        {showAndroidBuildStamp() ? <p className="muted">{ANDROID_BUILD_LABEL}</p> : null}
      </section>
      <AppFooter />
    </div>
  );
}

function hasIdToken(payload: unknown): payload is AuthenticationResult {
  return Boolean(
    payload &&
      typeof payload === "object" &&
      "idToken" in payload &&
      typeof (payload as AuthenticationResult).idToken === "string" &&
      (payload as AuthenticationResult).idToken &&
      (payload as AuthenticationResult).account,
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { instance, inProgress, accounts } = useMsal();
  const [checked, setChecked] = useState(false);
  const [tokenOk, setTokenOk] = useState(false);
  const [nativeOk, setNativeOk] = useState(hasNativeSession());
  const [loginError, setLoginError] = useState<string | undefined>(readNativeLoginError());

  useEffect(() => subscribeNativeSession(() => setNativeOk(hasNativeSession())), []);

  useEffect(() => {
    const onSettled = () => {
      setLoginError(readNativeLoginError());
      setNativeOk(hasNativeSession());
    };
    window.addEventListener("ironfleet-auth-settled", onSettled);
    return () => window.removeEventListener("ironfleet-auth-settled", onSettled);
  }, []);

  useEffect(() => {
    const id = instance.addEventCallback((message) => {
      if (message.eventType === EventType.LOGIN_SUCCESS && hasIdToken(message.payload)) {
        sessionStorage.removeItem(NATIVE_LOGIN_ERROR_KEY);
        setLoginError(undefined);
        if (!isNativeShell()) {
          grantNativeSession();
          setTokenOk(true);
          setChecked(true);
        }
      }
      if (message.eventType === EventType.LOGOUT_SUCCESS) {
        setTokenOk(false);
      }
    });
    return () => {
      if (id) {
        instance.removeEventCallback(id);
      }
    };
  }, [instance]);

  useEffect(() => {
    if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
      return;
    }
    let cancelled = false;
    void (async () => {
      if (isNativeShell()) {
        if (!cancelled) {
          setLoginError(readNativeLoginError());
          setChecked(true);
        }
        return;
      }
      try {
        await acquireIdToken(instance);
        if (!cancelled) {
          setTokenOk(true);
        }
      } catch {
        if (!cancelled) {
          setTokenOk(false);
        }
      } finally {
        if (!cancelled) {
          setChecked(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accounts, inProgress, instance]);

  useEffect(() => {
    if (!checked || !tokenOk) {
      return;
    }
    window.dispatchEvent(new Event("ironfleet-auth-settled"));
  }, [checked, tokenOk, accounts[0]?.homeAccountId]);

  if (isNativeShell()) {
    if (!checked && (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect)) {
      return (
        <div className="app-shell login-shell">
          <p className="brand-title">Finishing sign-in…</p>
        </div>
      );
    }
    if (!nativeOk) {
      return <LoginScreen error={loginError} />;
    }
    return children;
  }

  if (
    !checked ||
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect
  ) {
    return (
      <div className="app-shell login-shell">
        <p className="brand-title">Finishing sign-in…</p>
      </div>
    );
  }

  if (!tokenOk) {
    return <LoginScreen error={loginError} />;
  }

  return children;
}
